import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, ShieldCheck } from "lucide-react";
import { TEAM, VALUES, CERTIFICATIONS, STATS, COMPANY } from "@/lib/data";
import SEO, { ORGANIZATION_SCHEMA } from "@/components/SEO";

export default function ChiSiamo() {
    return (
        <>
            <SEO
                title="Chi Siamo · 15+ anni di formazione salvavita"
                description="7MED GROUP è il principale fornitore di corsi BLS-AED e primo soccorso in Ticino. 15+ anni di esperienza, 6'500+ persone formate, team certificato SRC ed EduQua, docenti USTRA."
                path="/chi-siamo"
                keywords="7MED chi siamo, team 7MED, Naike Romeo, formazione SRC Ticino, EduQua"
                structuredData={[
                    ORGANIZATION_SCHEMA,
                    {
                        "@context": "https://schema.org",
                        "@type": "AboutPage",
                        url: "https://7med.ch/chi-siamo",
                        about: { "@id": "https://7med.ch/#organization" },
                    },
                ]}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Chi Siamo", path: "/chi-siamo" }]}
            />
            <section className="bg-white">
                <div className="container-7m py-16 sm:py-20 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow text-brand">Chi siamo</div>
                            <h1 className="mt-4 font-heading font-black text-4xl sm:text-6xl lg:text-8xl tracking-tighter leading-[1.05]">
                                Il tuo partner N°1<br />
                                <span className="text-brand">per la formazione</span> salvavita.
                            </h1>
                            <p className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-readable-lg max-w-2xl">
                                La nostra missione è rendere le conoscenze di primo soccorso accessibili, coinvolgenti e durature per tutti.
                                Vogliamo che ogni partecipante torni alla vita quotidiana con più consapevolezza, fiducia e capacità di intervenire quando davvero conta.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 grid grid-cols-2 gap-px bg-ink/10 self-start">
                            {STATS.slice(0, 4).map((s) => (
                                <div key={s.label} className="bg-white p-6">
                                    <div className="font-heading font-black text-3xl tracking-tighter">{s.value}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-mono text-ink-muted">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Storia / mission */}
            <section className="py-20 lg:py-28 bg-[#FAFAFA] border-y border-ink/10">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Mission</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Salvare vite attraverso la conoscenza.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-ink-muted leading-relaxed">
                        <p>
                            Siamo tra i principali fornitori di <strong className="text-ink">corsi di primo soccorso per aziende e scuole</strong> in Ticino e in Svizzera. Il nostro team, esperto e qualificato, ha come obiettivo rendere ogni ambiente di lavoro più sicuro e preparato alle emergenze.
                        </p>
                        <p>
                            Offriamo anche corsi per privati, vendita e manutenzione di defibrillatori AED.
                            Operiamo principalmente in Ticino, ma anche nella <strong className="text-ink">Svizzera tedesca e romanda</strong>: i nostri corsi possono essere prenotati in italiano, tedesco e francese.
                        </p>
                    </div>
                </div>
            </section>

            {/* Valori */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
                        <div className="lg:col-span-5">
                            <div className="label-eyebrow text-brand">I nostri valori</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Principi che guidano ogni decisione.</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10">
                        {VALUES.map((v) => (
                            <div key={v.title} className="bg-white p-8 hover:bg-ink hover:text-white transition-colors duration-300 group">
                                <v.icon className="w-7 h-7 text-brand" strokeWidth={1.5} />
                                <h3 className="mt-6 font-heading font-bold text-2xl tracking-tight">{v.title}</h3>
                                <p className="mt-3 text-sm text-ink-muted group-hover:text-white/70 leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="py-20 lg:py-28 bg-ink text-white">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
                        <div className="lg:col-span-5">
                            <div className="label-eyebrow !text-brand">Il nostro team</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Professionisti appassionati.</h2>
                        </div>
                        <p className="lg:col-span-6 lg:col-start-7 text-white/60 leading-relaxed">
                            Anni di esperienza sul campo, certificazioni SRC e USTRA, e un'unica passione: trasmettere conoscenze che fanno la differenza.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {TEAM.map((m) => (
                            <div key={m.name} className="bg-ink-soft p-6 flex flex-col" data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                <div className="aspect-[4/5] bg-white/5 overflow-hidden">
                                    {m.image ? (
                                        <img src={m.image} alt={m.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <span className="font-heading font-black text-7xl text-brand">{m.name.split(" ").map((s) => s[0]).join("")}</span>
                                        </div>
                                    )}
                                </div>
                                <h3 className="mt-5 font-heading font-bold text-xl">{m.name}</h3>
                                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand mt-1">{m.role}</div>
                                <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{m.bio}</p>
                                {m.email && (
                                    <a href={`mailto:${m.email}`} className="mt-4 text-sm font-mono text-white/80 hover:text-brand transition-colors break-all" data-testid={`team-email-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                        {m.email}
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Certificazioni */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Certificazioni</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                            Ente di formazione riconosciuto.
                        </h2>
                        <p className="mt-6 text-ink-muted leading-relaxed">
                            Garanzia di qualità, competenza e conformità agli standard ufficiali del primo soccorso in Svizzera.
                        </p>
                    </div>
                    <div className="lg:col-span-7 space-y-px bg-ink/10">
                        {CERTIFICATIONS.map((c) => (
                            <div key={c} className="bg-white flex items-center gap-4 p-6">
                                <ShieldCheck className="w-6 h-6 text-brand" strokeWidth={1.5} />
                                <span className="font-heading font-semibold text-lg">{c}</span>
                            </div>
                        ))}
                        <div className="bg-white p-6">
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted mb-3">Loghi & accreditamenti</div>
                            <div className="flex flex-wrap gap-3">
                                {["SRC · Swiss Resuscitation Council", "Confederazione Svizzera", "EduQua", "SECO · OLL3"].map((x) => (
                                    <span key={x} className="px-3 py-2 border border-ink text-xs font-mono uppercase tracking-wider">{x}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-brand text-white">
                <div className="container-7m py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-heading font-black text-4xl lg:text-6xl tracking-tighter leading-none">Vieni a trovarci.</h2>
                        <p className="mt-6 text-lg text-white/85">Il nostro ufficio è sempre aperto per consulenze e informazioni.</p>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9 space-y-3 text-sm">
                        <div className="flex items-center gap-3"><MapPin className="w-5 h-5" />Via Luigi Lavizzari 2, 6850 Mendrisio</div>
                        <div className="flex items-center gap-3"><Phone className="w-5 h-5" />{COMPANY.phone}</div>
                        <div className="flex items-center gap-3"><Mail className="w-5 h-5" />{COMPANY.email}</div>
                        <Link to="/contatti" className="inline-flex items-center gap-2 mt-4 font-bold uppercase tracking-wider text-sm bg-white text-ink px-6 py-3 hover:bg-ink hover:text-white transition-colors">Tutti i contatti <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </section>
        </>
    );
}
